import { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useMutation } from "react-apollo"
import classNames from "classnames"
import { UPDATE_PROFILE_SECTION } from "./queries"
import { IconClose, IconPencil } from "./icons"

export const errorText = (e) =>
  String((e && e.message) || "Something went wrong. Please try again.").replace("GraphQL error: ", "")

// save(section, input) for the profile's sections; resolves to true on success
export const useSaveSection = () => {
  const [mutate] = useMutation(UPDATE_PROFILE_SECTION)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const save = async (section, input) => {
    setBusy(true)
    setError("")
    try {
      await mutate({ variables: { section, input } })
      setBusy(false)
      return true
    } catch (e) {
      setBusy(false)
      setError(errorText(e))
      return false
    }
  }
  return { save, busy, error, setError }
}

export const EditButton = ({ onClick, label = "Edit", hidden }) => (
  <button type="button" className="fp-edit" onClick={onClick} aria-label={hidden ? label : undefined}>
    <IconPencil />
    {!hidden && <span>{label}</span>}
  </button>
)

export const Spinner = () => <span className="fp-spin" aria-hidden="true" />

// The blue-ringed card a section turns into while it's edited
export const EditorCard = ({ title, titleId, hint, children, onSave, onCancel, busy, error, saveLabel = "Save", extra }) => {
  const ref = useRef(null)
  useEffect(() => {
    const first = ref.current && ref.current.querySelector("input, textarea, select, button.fp-pickable")
    if (first) first.focus()
  }, [])
  return (
    <section className="fp-card fp-editor" aria-labelledby={titleId} ref={ref}>
      {title && (
        <h2 className="fp-h2" id={titleId}>
          {title}
        </h2>
      )}
      {hint && <p className="fp-hint">{hint}</p>}
      {children}
      {error && (
        <p className="fp-error" role="alert">
          {error}
        </p>
      )}
      <div className="fp-editor-foot">
        {extra}
        <span className="fp-grow" />
        <button type="button" className="fp-btn fp-btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="fp-btn" onClick={onSave} disabled={busy}>
          {busy ? <Spinner /> : null}
          {busy ? "Saving…" : saveLabel}
        </button>
      </div>
    </section>
  )
}

// Centered dialog; Escape and the backdrop close it
export const Modal = ({ title, titleId, onClose, children, wide }) => {
  const box = useRef(null)
  useEffect(() => {
    const prev = document.activeElement
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    document.body.classList.add("fp-modal-open")
    const first = box.current && box.current.querySelector("button, input, textarea, select, [tabindex]")
    if (first) first.focus()
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.classList.remove("fp-modal-open")
      if (prev && prev.focus) prev.focus()
    }
  }, [])
  return ReactDOM.createPortal(
    <div className="fp-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={classNames("fp-modal-box", { "fp-modal-box--wide": wide })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={box}
      >
        <div className="fp-modal-head">
          <h2 className="fp-h2" id={titleId}>
            {title}
          </h2>
          <button type="button" className="fp-icon-btn" aria-label="Close" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  )
}

export const Pill = ({ tone, children }) => <span className={classNames("fp-pill", tone && `fp-pill--${tone}`)}>{children}</span>

// Choice button used by the editors' pickers
export const Pickable = ({ on, onClick, children, disabled }) => (
  <button
    type="button"
    className={classNames("fp-pickable", { "fp-on": on })}
    aria-pressed={on ? "true" : "false"}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
)
